import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Search, X, Play, ChevronLeft, ChevronRight, MessageCircle, Phone, ArrowRight, Truck, CreditCard, Shield, Tag, ShoppingBag, Clock, User, LogIn } from 'lucide-react';
import { getColorHex } from '../lib/colors';
import VitrinePromocional from '../components/VitrinePromocional';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Banner Carousel
const BannerCarousel = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  if (!banners || banners.length === 0) {
    return (
      <div className="relative h-[300px] md:h-[420px] bg-gradient-to-r from-orange-600 to-orange-500 flex items-center">
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="max-w-lg">
            <span className="inline-block bg-white/20 text-white text-sm font-medium px-3 py-1 rounded-full mb-4">
              NOVIDADES
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Embalagens de Qualidade para seu Negócio
            </h1>
            <p className="text-white/90 text-lg mb-6">
              Encontre tudo que você precisa em um só lugar
            </p>
            <a href="#produtos" className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors">
              Ver Produtos <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  const banner = banners[currentIndex];

  return (
    <div className="relative h-[300px] md:h-[420px] overflow-hidden" data-testid="banner-carousel">
      {/* Banner Background Image */}
      {banner.image_url ? (
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-700"
          style={{ backgroundImage: `url(${banner.image_url})` }}
          key={currentIndex}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-500" />
      )}
      
      {/* Overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-transparent" />
      
      {/* Text Content */}
      <div className="relative h-full max-w-7xl mx-auto px-4 flex items-center z-[1]">
        <div className="max-w-lg">
          {banner.subtitle && (
            <span className="inline-block bg-orange-500 text-white text-sm font-medium px-3 py-1 rounded-full mb-4">
              {banner.subtitle}
            </span>
          )}
          {banner.title && (
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
              {banner.title}
            </h2>
          )}
          {banner.description && (
            <p className="text-white/90 text-lg mb-6 drop-shadow">
              {banner.description}
            </p>
          )}
          {banner.button_text && (
            <a 
              href={banner.button_link || '#produtos'}
              className="inline-flex items-center gap-2 bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors shadow-lg"
            >
              {banner.button_text} <ArrowRight className="w-5 h-5" />
            </a>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      {banners.length > 1 && (
        <>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {banners.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-3 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/80 w-3'
                }`}
                data-testid={`banner-dot-${idx}`}
              />
            ))}
          </div>
          <button
            onClick={() => setCurrentIndex(prev => (prev - 1 + banners.length) % banners.length)}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all z-10"
            data-testid="banner-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() => setCurrentIndex(prev => (prev + 1) % banners.length)}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all z-10"
            data-testid="banner-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
    </div>
  );
};

// Benefícios
const Benefits = () => (
  <div className="bg-white border-y border-gray-100 py-4">
    <div className="max-w-7xl mx-auto px-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Clock, title: 'A Prazo', desc: 'Sob Análise de Crédito' },
          { icon: Tag, title: 'Descontos', desc: 'Para atacado' },
          { icon: Truck, title: 'Entrega', desc: 'Para todo Brasil' },
          { icon: Shield, title: 'Site Seguro', desc: 'Compra garantida' }
        ].map((item, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <item.icon className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{item.title}</p>
              <p className="text-xs text-gray-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Seções de Destaque
const FeaturedSections = ({ sections, onCategoryClick }) => {
  if (!sections || sections.length === 0) return null;
  
  return (
    <section className="py-10 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Veja nossas ofertas</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sections.map((section, idx) => (
            <div
              key={section.id || idx}
              onClick={() => onCategoryClick(section.category)}
              className="relative group cursor-pointer overflow-hidden rounded-xl aspect-[3/4] shadow-md hover:shadow-xl transition-shadow"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ 
                  backgroundImage: section.image_url 
                    ? `url(${section.image_url})` 
                    : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                <p className="text-xs opacity-80 uppercase tracking-wider">{section.subtitle || 'Categoria'}</p>
                <h3 className="text-lg font-bold">{section.title}</h3>
                <p className="text-sm font-medium text-orange-300 flex items-center gap-1 mt-1 group-hover:gap-2 transition-all">
                  Conferir <ArrowRight className="w-4 h-4" />
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Coleções (Categorias em círculo)
const Collections = ({ categories, onCategoryClick }) => {
  if (!categories || categories.length === 0) return null;
  
  return (
    <section className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Coleções</h2>
        <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
          {categories.slice(0, 8).map((cat, idx) => (
            <button
              key={cat.id || idx}
              onClick={() => onCategoryClick(cat.name)}
              className="flex flex-col items-center gap-2 flex-shrink-0 group"
            >
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 p-1 group-hover:from-orange-200 group-hover:to-orange-300 transition-colors">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  {cat.image_url ? (
                    <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                  ) : (
                    <ShoppingBag className="w-8 h-8 text-orange-400" />
                  )}
                </div>
              </div>
              <span className="text-sm font-medium text-gray-700 text-center max-w-[80px] line-clamp-2">
                {cat.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// Card de Produto
const ProductCard = ({ product, onClick }) => {
  const mainImage = product.fotos?.[0] || null;
  
  return (
    <div 
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group border border-gray-100"
      onClick={() => onClick(product)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {mainImage ? (
          <img 
            src={mainImage} 
            alt={product.produto}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-5xl font-bold text-gray-300">
              {(product.produto || 'P')[0]}
            </span>
          </div>
        )}
        {product.videos?.length > 0 && (
          <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <Play className="w-3 h-3" /> Vídeo
          </div>
        )}
      </div>
      <div className="p-4">
        <span className="text-xs text-orange-600 font-semibold uppercase tracking-wider">
          {product.categoria || 'Produto'}
        </span>
        <h3 className="font-medium text-gray-800 mt-1 line-clamp-2 group-hover:text-orange-600 transition-colors">
          {product.produto}
        </h3>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500">Cód: {product.codigo}</span>
          {product.tamanho && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
              {product.tamanho}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal de Produto
const ProductModal = ({ product, onClose, whatsappNumber }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariacaoImg, setSelectedVariacaoImg] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ nome: '', estrelas: 5, comentario: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeTab, setActiveTab] = useState('detalhes');
  
  const images = product.fotos?.length > 0 ? product.fotos : [];
  const videos = product.videos || [];
  const specImages = product.fotos_especificacoes || [];
  // Foto da variação selecionada entra como PRIMEIRA imagem (prioriza no carrossel)
  const allImages = selectedVariacaoImg
    ? [selectedVariacaoImg, ...images, ...specImages]
    : [...images, ...specImages];
  const cores = product.cores || [];
  const coresFotoMap = product.cores_foto_map || {};
  const [selectedCor, setSelectedCor] = useState(null);

  // Quando o cliente clica numa cor, salta para a foto vinculada (se existir)
  const handleCorClick = (cor) => {
    setSelectedCor(cor);
    const fotoCor = coresFotoMap[cor];
    if (!fotoCor) return;
    const idx = allImages.findIndex(img => img === fotoCor || img.endsWith(fotoCor) || fotoCor.endsWith(img));
    if (idx >= 0) {
      setShowVideo(false);
      setCurrentImageIndex(idx);
    }
  };

  useEffect(() => {
    axios.post(`${API}/public/analytics/product-view`, { product_id: product.id, product_name: product.produto }).catch(() => {});
    axios.get(`${API}/public/reviews/${product.id}`).then(r => setReviews(r.data || [])).catch(() => {});
  }, [product.id, product.produto]);

  const handleVideoPlay = (index) => { setCurrentVideoIndex(index); setShowVideo(true); };

  const handleSubmitReview = async () => {
    if (!reviewForm.nome.trim() || !reviewForm.comentario.trim()) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/public/reviews`, { product_id: product.id, product_name: product.produto, ...reviewForm });
      setShowReviewForm(false);
      setReviewForm({ nome: '', estrelas: 5, comentario: '' });
      alert('Review enviada! Aguardando aprovação.');
    } catch { /* ignore */ }
    setSubmittingReview(false);
  };

  const whatsappLink = whatsappNumber 
    ? `https://wa.me/55${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Gostaria de saber sobre o produto: ${product.produto} (Cód: ${product.codigo})`)}`
    : '#';
  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.estrelas, 0) / reviews.length).toFixed(1) : null;

  // Color name to CSS color mapping (helper centralizado)
  const getColor = (name) => getColorHex(name);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col lg:flex-row h-full">
          {/* LEFT: Gallery */}
          <div className="lg:w-[55%] bg-gray-50 flex flex-col">
            {/* Main Image */}
            <div className="relative flex-1 min-h-[300px] lg:min-h-[400px]">
              {showVideo && videos[currentVideoIndex] ? (
                <video src={videos[currentVideoIndex]} controls autoPlay className="w-full h-full object-contain bg-black" />
              ) : allImages.length > 0 ? (
                <img src={allImages[currentImageIndex]} alt={product.produto} className="w-full h-full object-contain p-4" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-[120px] font-bold text-gray-200">{(product.produto || 'P')[0]}</span>
                </div>
              )}
              <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg z-10" data-testid="modal-close">
                <X className="w-5 h-5" />
              </button>
              {allImages.length > 1 && !showVideo && (
                <>
                  <button onClick={() => setCurrentImageIndex(i => i > 0 ? i - 1 : allImages.length - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => setCurrentImageIndex(i => i < allImages.length - 1 ? i + 1 : 0)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 p-2 rounded-full shadow"><ChevronRight className="w-4 h-4" /></button>
                </>
              )}
            </div>
            {/* Thumbnails */}
            {(allImages.length > 1 || videos.length > 0) && (
              <div className="p-2 flex gap-1.5 overflow-x-auto bg-white border-t">
                {allImages.map((img, i) => (
                  <button key={i} onClick={() => { setShowVideo(false); setCurrentImageIndex(i); }}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${!showVideo && currentImageIndex === i ? 'border-orange-500' : 'border-gray-200'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {videos.map((_, i) => (
                  <button key={`v-${i}`} onClick={() => handleVideoPlay(i)}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg bg-gray-900 flex items-center justify-center border-2 ${showVideo && currentVideoIndex === i ? 'border-orange-500' : 'border-gray-200'}`}>
                    <Play className="w-5 h-5 text-white" />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* RIGHT: Product Info */}
          <div className="lg:w-[45%] flex flex-col max-h-[95vh] lg:max-h-none">
            <div className="flex-1 overflow-y-auto p-5">
              {/* Category + Rating */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded-full">{product.categoria || 'Produto'}</span>
                {avgRating && <span className="flex items-center gap-1 text-xs text-yellow-600 font-medium">{'★'.repeat(Math.round(parseFloat(avgRating)))} {avgRating}</span>}
              </div>
              
              {/* Name */}
              <h2 className="text-xl font-bold text-gray-900 mt-2 leading-tight">{product.produto}</h2>
              <p className="text-sm text-gray-500 mt-1">Código: <strong>{product.codigo}</strong> {product.tamanho && <>• Tamanho: <strong>{product.tamanho}</strong></>}</p>

              {/* Colors as circles - clica para ver a foto vinculada à cor */}
              {cores.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-xs font-bold text-gray-700 uppercase mb-1.5">
                    Cores: {selectedCor && <span className="text-orange-600 normal-case font-semibold">— {selectedCor}</span>}
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {cores.map((cor, i) => {
                      const temFoto = !!coresFotoMap[cor];
                      const isSelected = selectedCor === cor;
                      return (
                        <button
                          type="button"
                          key={i}
                          onClick={() => handleCorClick(cor)}
                          className="group relative"
                          data-testid={`color-circle-${cor}`}
                          title={temFoto ? `Clique para ver a foto de ${cor}` : cor}
                        >
                          <div
                            className={`w-8 h-8 rounded-full border-2 cursor-pointer transition-all shadow-sm ${isSelected ? 'border-orange-500 ring-2 ring-orange-300 scale-110' : 'border-gray-200 hover:border-orange-400'}`}
                            style={{ backgroundColor: getColor(cor) }}
                          />
                          {temFoto && (
                            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-orange-500 border-2 border-white" title="Foto vinculada" />
                          )}
                          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] bg-gray-800 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">{cor}</span>
                        </button>
                      );
                    })}
                  </div>
                  {Object.keys(coresFotoMap).length > 0 && (
                    <p className="text-[10px] text-gray-400 mt-1.5 italic">💡 Cores com bolinha laranja mostram a foto correspondente ao clicar.</p>
                  )}
                </div>
              )}

              {/* Variações — LISTA compacta (miniatura + código); clique troca a imagem principal à esquerda */}
              {Array.isArray(product.variacoes) && product.variacoes.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-xs font-bold text-gray-700 uppercase mb-1.5">
                    Variações ({product.variacoes.length}):{' '}
                    {selectedCor && <span className="text-orange-600 normal-case font-semibold">— {selectedCor}</span>}
                  </h4>
                  <div className="max-h-[280px] overflow-y-auto border border-gray-200 rounded-md bg-gray-50 divide-y divide-gray-100" data-testid="modal-variacoes-list">
                    {product.variacoes.map((v, i) => {
                      const isActive = selectedCor === v.codigo;
                      return (
                        <button
                          key={v.codigo || i}
                          type="button"
                          onClick={() => {
                            setSelectedCor(v.codigo);
                            if (v.imagem) {
                              setSelectedVariacaoImg(v.imagem);
                              setShowVideo(false);
                              setCurrentImageIndex(0);
                            }
                          }}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition ${isActive ? 'bg-orange-50 border-l-4 border-orange-500' : 'bg-white hover:bg-gray-50 border-l-4 border-transparent'}`}
                          data-testid={`variacao-btn-${(v.codigo || i).toString().toLowerCase()}`}
                          title={v.descricao || v.codigo}
                        >
                          <div className="w-9 h-9 rounded border border-gray-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                            {v.imagem ? (
                              <img src={v.imagem} alt={v.codigo} className="w-full h-full object-contain" loading="lazy" />
                            ) : (
                              <span className="text-gray-400 text-[10px]">-</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-mono font-bold truncate ${isActive ? 'text-orange-600' : 'text-gray-800'}`}>
                              {v.codigo}
                            </div>
                            {v.descricao && (
                              <div className="text-[10px] text-gray-500 truncate italic">{v.descricao}</div>
                            )}
                          </div>
                          {v.tipo && v.tipo !== 'modelo' && (
                            <span className="text-[9px] uppercase font-bold text-gray-400 flex-shrink-0">{v.tipo}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1.5 italic">💡 Clique numa variação para ver a foto correspondente ao lado.</p>
                </div>
              )}

              {/* Tabs */}
              <div className="mt-4 flex gap-1 border-b border-gray-200">
                {['detalhes', 'reviews'].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-all ${activeTab === tab ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    {tab === 'detalhes' ? 'Detalhes' : `Reviews (${reviews.length})`}
                  </button>
                ))}
              </div>

              {activeTab === 'detalhes' && (
                <div className="mt-3 space-y-3">
                  {product.descricao && <div><h3 className="text-xs font-bold text-gray-700 uppercase">Descrição</h3><p className="text-gray-600 text-sm mt-1 leading-relaxed">{product.descricao}</p></div>}
                  {product.detalhes && <div><h3 className="text-xs font-bold text-gray-700 uppercase">Detalhes do Produto</h3><p className="text-gray-600 text-sm mt-1 leading-relaxed whitespace-pre-line">{product.detalhes}</p></div>}
                  {product.especificacoes && <div><h3 className="text-xs font-bold text-gray-700 uppercase">Especificações</h3><p className="text-gray-600 text-sm mt-1 leading-relaxed whitespace-pre-line">{product.especificacoes}</p></div>}
                  
                  {/* Spec Images Grid */}
                  {specImages.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold text-gray-700 uppercase">Detalhes do Produto</h3>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {specImages.map((img, i) => (
                          <div key={i} className="aspect-square rounded-lg border-2 border-orange-200 overflow-hidden cursor-pointer hover:border-orange-400 transition-colors"
                            onClick={() => { setShowVideo(false); setCurrentImageIndex(images.length + i); }}>
                            <img src={img} alt={`Detalhe ${i+1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="mt-3 space-y-2">
                  {reviews.length === 0 && <p className="text-gray-400 text-sm py-2">Nenhuma review ainda. Seja o primeiro!</p>}
                  {reviews.map((r, i) => (
                    <div key={r.id || i} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-yellow-500 text-sm">{'★'.repeat(r.estrelas)}{'☆'.repeat(5 - r.estrelas)}</span>
                        <span className="font-medium text-sm">{r.nome}</span>
                      </div>
                      <p className="text-gray-600 text-sm mt-1">{r.comentario}</p>
                    </div>
                  ))}
                  {!showReviewForm ? (
                    <button onClick={() => setShowReviewForm(true)} className="text-orange-600 text-sm font-medium hover:underline" data-testid="write-review-btn">Escrever Review</button>
                  ) : (
                    <div className="bg-orange-50 p-3 rounded-lg space-y-2 border border-orange-100">
                      <input type="text" placeholder="Seu nome" value={reviewForm.nome} onChange={e => setReviewForm(p => ({ ...p, nome: e.target.value }))}
                        className="w-full p-2 border rounded text-sm" data-testid="review-name" />
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setReviewForm(p => ({ ...p, estrelas: n }))}
                            className={`text-xl ${n <= reviewForm.estrelas ? 'text-yellow-500' : 'text-gray-300'}`}>★</button>
                        ))}
                      </div>
                      <textarea placeholder="Seu comentário..." value={reviewForm.comentario} onChange={e => setReviewForm(p => ({ ...p, comentario: e.target.value }))}
                        className="w-full p-2 border rounded text-sm h-16 resize-none" data-testid="review-comment" />
                      <div className="flex gap-2">
                        <button onClick={handleSubmitReview} disabled={submittingReview} className="px-3 py-1.5 bg-orange-500 text-white rounded text-sm font-medium hover:bg-orange-600" data-testid="submit-review-btn">
                          {submittingReview ? 'Enviando...' : 'Enviar'}
                        </button>
                        <button onClick={() => setShowReviewForm(false)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm">Cancelar</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* WhatsApp CTA - Fixed bottom */}
            <div className="p-4 border-t bg-white">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-xl font-semibold transition-colors shadow-lg"
                data-testid="product-whatsapp-btn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                Gostaria de saber sobre esse produto
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// WhatsApp Button
const WhatsAppButton = ({ phoneNumber, isOpen, onToggle }) => {
  if (!phoneNumber) return null;
  const whatsappUrl = `https://wa.me/55${phoneNumber.replace(/\D/g, '')}`;
  
  return (
    <div className="fixed bottom-6 right-6 z-40">
      {isOpen && (
        <div className="absolute bottom-20 right-0 bg-white rounded-2xl shadow-2xl p-5 w-80 mb-2 animate-in slide-in-from-bottom-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
              <Phone className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900">Santa Ana</p>
              <p className="text-xs text-green-600 flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> Online
              </p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">Olá! 👋 Como podemos ajudar?</p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block w-full bg-gradient-to-r from-green-500 to-green-600 text-white text-center py-3 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg">
            Iniciar Conversa
          </a>
        </div>
      )}
      <button
        onClick={onToggle}
        className={`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-gray-700 rotate-180' : 'bg-gradient-to-br from-green-400 to-green-600 hover:scale-110'
        }`}
      >
        {isOpen ? <X className="w-7 h-7 text-white" /> : <MessageCircle className="w-8 h-8 text-white" />}
      </button>
    </div>
  );
};

// Componente Principal
export const Catalogo = () => {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [topBarText, setTopBarText] = useState('');
  const [marqueeTexts, setMarqueeTexts] = useState([]);
  const [marqueeSettings, setMarqueeSettings] = useState({});
  const [layoutSections, setLayoutSections] = useState([]);
  const [logoBase64, setLogoBase64] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storeMapsUrl, setStoreMapsUrl] = useState('');
  const [storeCnpj, setStoreCnpj] = useState('');
  const [storeNomeFantasia, setStoreNomeFantasia] = useState('');
  const [storeEmail, setStoreEmail] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, settingsRes, bannersRes, sectionsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/public/products`),
          axios.get(`${API}/public/settings`),
          axios.get(`${API}/public/banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/sections`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/categories`).catch(() => ({ data: [] }))
        ]);
        
        setProducts(prodRes.data);
        const settings = settingsRes.data || {};
        setWhatsappNumber(settings.whatsapp_number || '');
        setTopBarText(settings.top_bar_text || '');
        setLogoBase64(settings.logo_base64 || '');
        setStoreAddress(settings.store_address || '');
        setStoreMapsUrl(settings.store_maps_url || '');
        setStoreCnpj(settings.store_cnpj || '');
        setStoreNomeFantasia(settings.store_nome_fantasia || '');
        setStoreEmail(settings.store_email || '');
        // Marquee
        const texts = (settings.marquee_texts || []).filter(t => t.trim());
        setMarqueeTexts(texts.length > 0 ? texts : (settings.top_bar_text ? [settings.top_bar_text] : []));
        setMarqueeSettings({
          bg_color: settings.marquee_bg_color || '#ea580c',
          text_color: settings.marquee_text_color || '#ffffff',
          speed: settings.marquee_speed || 'normal',
          glow: settings.marquee_glow || false,
          blink: settings.marquee_blink || false
        });
        setLayoutSections(settings.layout_sections || []);
        setBanners(bannersRes.data || []);
        setSections(sectionsRes.data || []);
        setCategories(categoriesRes.data || []);
        
        axios.post(`${API}/public/analytics/page-view`, { 
          page: 'catalogo',
          language: navigator.language || '',
          referrer: document.referrer || '',
          screen_width: window.screen?.width,
          screen_height: window.screen?.height
        }).catch(() => {});
      } catch (error) {
        console.error('Erro:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(category);
    setShowAllProducts(true);
    setTimeout(() => {
      document.getElementById('produtos')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleProductClick = useCallback((product) => {
    setSelectedProduct(product);
    axios.post(`${API}/public/analytics/product-click`, {
      product_id: product.id,
      product_name: product.produto
    }).catch(() => {});
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchSearch = !searchTerm || 
        p.produto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codigo?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory = !selectedCategory || p.categoria === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const productsByCategory = useMemo(() => {
    const grouped = {};
    products.forEach(p => {
      const cat = p.categoria || 'Outros';
      if (!grouped[cat]) grouped[cat] = [];
      if (grouped[cat].length < 6) grouped[cat].push(p);
    });
    return grouped;
  }, [products]);

  // Agrupamento completo (sem limite de 6) para modo "Ver Todos os Produtos".
  // Produtos sem categoria vão para "Sem Categoria" e são renderizados por ÚLTIMO.
  const allProductsGrouped = useMemo(() => {
    const grouped = {};
    products.forEach(p => {
      const cat = (p.categoria || '').trim();
      const key = cat || '__sem_categoria__';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(p);
    });
    // Ordena alfabeticamente as categorias, mantendo "__sem_categoria__" no final
    const entries = Object.entries(grouped).sort(([a], [b]) => {
      if (a === '__sem_categoria__') return 1;
      if (b === '__sem_categoria__') return -1;
      return a.localeCompare(b, 'pt-BR');
    });
    return entries.map(([key, prods]) => ({
      label: key === '__sem_categoria__' ? 'Sem Categoria' : key,
      products: prods,
    }));
  }, [products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Marquee Top Bar */}
      {marqueeTexts.length > 0 && (
        <div className="overflow-hidden py-2" data-testid="marquee-bar"
          style={{ backgroundColor: marqueeSettings.bg_color, color: marqueeSettings.text_color }}>
          <div className={`marquee-track ${marqueeSettings.speed === 'slow' ? 'marquee-slow' : marqueeSettings.speed === 'fast' ? 'marquee-fast' : 'marquee-normal'}`}
            style={{ 
              textShadow: marqueeSettings.glow ? `0 0 10px ${marqueeSettings.text_color}, 0 0 20px ${marqueeSettings.text_color}` : 'none',
              animation: marqueeSettings.blink ? 'marquee-scroll var(--marquee-duration) linear infinite, marquee-blink 1s ease-in-out infinite' : 'marquee-scroll var(--marquee-duration) linear infinite'
            }}>
            <span className="text-sm font-medium whitespace-nowrap px-8">
              {marqueeTexts.join('     ★     ')}
            </span>
            <span className="text-sm font-medium whitespace-nowrap px-8">
              {marqueeTexts.join('     ★     ')}
            </span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 flex-shrink-0" data-testid="header-logo">
              {logoBase64 ? (
                <img 
                  src={logoBase64} 
                  alt="Santa Ana" 
                  className="h-12 w-auto"
                />
              ) : (
                <img 
                  src="/logo-santa-ana.png" 
                  alt="Santa Ana" 
                  className="h-12 w-auto"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 leading-tight">Santa Ana</h1>
                <p className="text-xs text-orange-600 font-medium">Distribuidora</p>
              </div>
            </a>
            
            {/* Search */}
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar produtos..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setShowAllProducts(true); }}
                  className="w-full pl-12 pr-4 py-3 bg-gray-100 border-0 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Login Cliente/Vendedor (bonequinho com ícone de login em cima) */}
            <a
              href="/login"
              className="group relative flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-gray-100 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-all"
              title="Acesse sua conta — Cliente ou Vendedor"
              data-testid="cliente-login-icon"
            >
              <User className="w-5 h-5 text-gray-700 group-hover:text-blue-700 transition-colors" />
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md ring-2 ring-white group-hover:bg-blue-700 transition-colors">
                <LogIn className="w-3 h-3" strokeWidth={2.5} />
              </span>
            </a>

            {/* WhatsApp */}
            {whatsappNumber ? (
              <a
                href={`https://wa.me/55${whatsappNumber.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-full font-medium transition-colors"
                data-testid="whatsapp-header-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            ) : (
              <button
                onClick={() => setWhatsappOpen(true)}
                className="hidden md:flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-full font-medium transition-colors"
                data-testid="whatsapp-header-btn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
            )}
          </div>
        </div>
        
        {/* Categories Bar - AZUL */}
        <div className="bg-blue-700">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex gap-1 overflow-x-auto py-2 catalogo-scrollbar">
              <button
                onClick={() => { setSelectedCategory(''); setShowAllProducts(false); }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  !selectedCategory 
                    ? 'bg-white text-blue-700' 
                    : 'text-white hover:bg-white/20'
                }`}
                data-testid="category-all"
              >
                Todos
              </button>
              {categories.map(cat => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryClick(cat.name)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat.name 
                      ? 'bg-white text-blue-700' 
                      : 'text-white hover:bg-white/20'
                  }`}
                  data-testid={`category-${cat.name}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main>
        {!showAllProducts && !searchTerm && (() => {
          const sectionMap = {
            banner: <BannerCarousel key="banner" banners={banners} />,
            benefits: <Benefits key="benefits" />,
            sections: <FeaturedSections key="sections" sections={sections} onCategoryClick={handleCategoryClick} />,
            collections: <Collections key="collections" categories={categories} onCategoryClick={handleCategoryClick} />
          };
          const ordered = layoutSections.length > 0 
            ? layoutSections.filter(s => s.enabled !== false && s.id !== 'marquee' && s.id !== 'products').sort((a, b) => a.order - b.order)
            : [{ id: 'banner' }, { id: 'benefits' }, { id: 'sections' }, { id: 'collections' }];
          return ordered.map(s => sectionMap[s.id] || null);
        })()}

        {/* Vitrines Promocionais (configuráveis no Admin > Editor do Site) */}
        <section className="max-w-7xl mx-auto px-4 py-6">
          <VitrinePromocional portal="catalogo" onProductClick={handleProductClick} showPrice={false} />
        </section>

        {/* Products */}
        <section id="produtos" className="py-10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedCategory || (showAllProducts ? 'Todos os Produtos' : 'Nossos Produtos')}
                </h2>
                <p className="text-gray-500 mt-1">{filteredProducts.length} produto{filteredProducts.length !== 1 ? 's' : ''}</p>
              </div>
              {(selectedCategory || searchTerm) && (
                <button
                  onClick={() => { setSelectedCategory(''); setSearchTerm(''); setShowAllProducts(false); }}
                  className="text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1"
                >
                  <X className="w-4 h-4" /> Limpar
                </button>
              )}
            </div>

            {searchTerm || selectedCategory ? (
              filteredProducts.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                  {filteredProducts.map(product => (
                    <ProductCard key={product.id} product={product} onClick={handleProductClick} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Nenhum produto encontrado</h3>
                  <button onClick={() => { setSelectedCategory(''); setSearchTerm(''); setShowAllProducts(false); }} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors mt-4">
                    Ver todos
                  </button>
                </div>
              )
            ) : showAllProducts ? (
              // Catálogo completo agrupado por categoria (sem categoria por último)
              <div className="space-y-12" data-testid="catalog-all-grouped">
                {allProductsGrouped.map(({ label, products: prods }) => (
                  <div key={label} data-testid={`catalog-group-${label.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800">{label}</h3>
                      <span className="text-sm text-gray-500">{prods.length} produto{prods.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                      {prods.map(product => (
                        <ProductCard key={product.id} product={product} onClick={handleProductClick} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-12">
                {Object.entries(productsByCategory).slice(0, 4).map(([category, prods]) => (
                  <div key={category}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800">{category}</h3>
                      <button onClick={() => handleCategoryClick(category)} className="text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1 text-sm">
                        Ver todos <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                      {prods.map(product => (
                        <ProductCard key={product.id} product={product} onClick={handleProductClick} />
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="text-center">
                  <button onClick={() => setShowAllProducts(true)} className="px-8 py-3 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-colors">
                    Ver Todos os Produtos
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              {logoBase64 ? (
                <img src={logoBase64} alt="Santa Ana" className="h-12 w-auto mb-4 brightness-0 invert" />
              ) : (
                <img src="/logo-santa-ana.png" alt="Santa Ana" className="h-12 w-auto mb-4 brightness-0 invert" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <p className="text-gray-400 text-sm">Distribuidora Santa Ana - Qualidade e variedade em embalagens.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Categorias</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                {categories.slice(0, 5).map(cat => (
                  <li key={cat.name}>
                    <button onClick={() => handleCategoryClick(cat.name)} className="hover:text-white transition-colors">{cat.name}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              {whatsappNumber && (
                <a href={`https://wa.me/55${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 hover:text-green-300">
                  <Phone className="w-4 h-4" /> WhatsApp: +55 {whatsappNumber}
                </a>
              )}
              {storeCnpj && (
                <p className="mt-3 text-xs text-gray-400" data-testid="store-cnpj">
                  <span className="font-semibold text-gray-300">CNPJ:</span> {storeCnpj}
                </p>
              )}
              {storeNomeFantasia && (
                <p className="mt-1 text-xs text-gray-400" data-testid="store-nome-fantasia">
                  <span className="font-semibold text-gray-300">Nome Fantasia:</span> {storeNomeFantasia}
                </p>
              )}
              {storeEmail && (
                <p className="mt-1 text-xs text-gray-400" data-testid="store-email">
                  <span className="font-semibold text-gray-300">E-mail:</span>{' '}
                  <a href={`mailto:${storeEmail}`} className="hover:text-orange-400 transition-colors">
                    {storeEmail}
                  </a>
                </p>
              )}
              <div className="mt-4">
                <a href="/politica-privacidade" className="text-gray-400 hover:text-white text-sm transition-colors" data-testid="privacy-link">
                  Politica de Privacidade
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} Distribuidora Santa Ana. Todos os direitos reservados.
          </div>
        </div>

        {/* Endereço + Google Maps */}
        {(storeAddress || storeMapsUrl) && (
          <div className="mt-6 border-t border-gray-800" data-testid="store-location-section">
            <div className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-2 gap-6 items-start">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">📍 Onde nos encontrar</h4>
                {storeAddress && (
                  <p className="text-gray-300 text-sm whitespace-pre-line" data-testid="store-address">{storeAddress}</p>
                )}
              </div>
              {storeMapsUrl && (
                <div className="rounded-lg overflow-hidden border border-gray-700 h-56 w-full">
                  <iframe
                    title="Localização Distribuidora Santa Ana"
                    src={storeMapsUrl}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    data-testid="store-map-iframe"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </footer>

      {selectedProduct && <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} whatsappNumber={whatsappNumber} />}
      <WhatsAppButton phoneNumber={whatsappNumber} isOpen={whatsappOpen} onToggle={() => setWhatsappOpen(!whatsappOpen)} />
    </div>
  );
};

export default Catalogo;
